import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = localStorage.getItem("novatus_token");
    const u = localStorage.getItem("novatus_user");
    if (t && u) {
      try {
        setUser(JSON.parse(u));
      } catch (e) {
        console.warn("AuthContext: stored user JSON is corrupt — clearing", e);
        localStorage.clear();
      }
      // Refresh from server in the background so flags like
      // `onboarding_completed` reflect the latest backend state.
      api.get("/auth/me").then(({ data }) => {
        setUser(data);
        localStorage.setItem("novatus_user", JSON.stringify(data));
      }).catch((e) => {
        // 401 already handled by api interceptor; other errors are non-fatal.
        if (e?.response?.status !== 401) console.warn("AuthContext.refresh-on-mount failed", e);
      });
    }
    setLoading(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("novatus_user", JSON.stringify(data));
    } catch (e) {
      console.warn("AuthContext.refreshUser failed", e);
    }
  }, []);

  const login = useCallback((token, userData) => {
    localStorage.setItem("novatus_token", token);
    localStorage.setItem("novatus_user", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("novatus_token");
    localStorage.removeItem("novatus_user");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser, setUser }),
    [user, loading, login, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
